import * as R from 'ramda'
import { ref, toValue, type MaybeRefOrGetter } from 'vue'

import type {
  Event,
  Participant,
  SubParticipant,
} from '@collector/sportsapi-types'

import type { LineupModel } from '../interfaces/LineupModel'
import { useSubParticipants } from './useSubParticipants'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useLineupModel<TLineupModel extends LineupModel>(
  eventId: MaybeRefOrGetter<Event['id']>,
  participantId: MaybeRefOrGetter<Participant['id']>,
  triggerSortOnLineupModelUpdate: boolean,
  createLineupModel: (players: SubParticipant[]) => TLineupModel,
  toSortedLineupModel: (model: NoInfer<TLineupModel>) => NoInfer<TLineupModel>,
  transformPlayers?: (players: SubParticipant[]) => Promise<SubParticipant[]>,
) {
  const { fetchParticipantSubParticipants } = useSubParticipants()
  const lineup = ref<TLineupModel>(createLineupModel([]))
  const initialLineup = ref<TLineupModel>(createLineupModel([]))

  async function fetchLineup(): Promise<void> {
    const result = await fetchParticipantSubParticipants(
      toValue(eventId),
      toValue(participantId),
    )
    if (result) {
      const players = transformPlayers ? await transformPlayers(result) : result
      const lineupsModel = createLineupModel(players)
      lineup.value = toSortedLineupModel(lineupsModel)
      initialLineup.value = R.clone(lineup.value)
    }
  }

  function updateLineupModel(newLineups: LineupModel): void {
    lineup.value = triggerSortOnLineupModelUpdate
      ? toSortedLineupModel(newLineups as TLineupModel)
      : (newLineups as TLineupModel)
  }

  return {
    lineup,
    initialLineup,
    updateLineupModel,
    fetchLineup,
  }
}
